import { info, warn, debug } from "@/shared/services/logger";

const SOURCE = "sig-notif";

/** Identificadores das notificações exibidas na barra de mensagens do item. */
export const NOTIFICATION_IDS = {
  CONFIGURE_NEEDED: "notif_configure_needed",
  MANAGED: "notif_managed",
  SESSION_EXPIRED: "notif_session_expired",
  INSERT_FAILED: "notif_insert_failed",
} as const;

/**
 * Remove todas as notificações ativas do item de email atual.
 *
 * @param item - O item do mailbox que contém as mensagens de notificação.
 *
 * @example
 * ```typescript
 * removeAllNotifications(Office.context.mailbox.item);
 * ```
 */
export function removeAllNotifications(item: any): void {
  debug(SOURCE, `removeAllNotifications: removendo ${Object.keys(NOTIFICATION_IDS).length} notificações.`);
  Object.values(NOTIFICATION_IDS).forEach((id) => {
    if (item && item.notificationMessages) {
      item.notificationMessages.removeAsync(id, () => {});
    }
  });
}

/**
 * Exibe uma barra de notificação com botão de ação "Configurar" no item de email.
 *
 * @param message - A mensagem de notificação a ser exibida.
 * @param notificationId - Identificador único da notificação.
 *
 * @example
 * ```typescript
 * showActionBar("Configure sua assinatura.", NOTIFICATION_IDS.CONFIGURE_NEEDED);
 * ```
 */
export function showActionBar(message: string, notificationId: string): void {
  const item = Office.context.mailbox.item;
  if (!item) {
    warn(SOURCE, `showActionBar(${notificationId}): item nulo — notificação não exibida.`);
    return;
  }

  info(SOURCE, `showActionBar(${notificationId}): exibindo InsightMessage com ação "Configurar".`);
  item.notificationMessages.addAsync(notificationId, {
    type: Office.MailboxEnums.ItemNotificationMessageType.InsightMessage,
    message: message,
    icon: "Icon.16x16",
    actions: [{
      actionText: "Configurar",
      actionType: Office.MailboxEnums.ActionType.ShowTaskPane,
      commandId: "msgComposeOpenPaneButton",
      contextData: { source: notificationId }
    }]
  }, (r) => {
    if (r.status === Office.AsyncResultStatus.Succeeded) {
      info(SOURCE, `showActionBar(${notificationId}): InsightMessage exibida com sucesso.`);
      return;
    }
    warn(SOURCE, `showActionBar(${notificationId}): InsightMessage falhou (${r.error?.message}) → fallback InformationalMessage.`);
    item.notificationMessages.addAsync(notificationId + "_fb", {
      type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
      message: message + " Abra o painel Helpitech para configurar.",
      icon: "Icon.16x16",
      persistent: true,
    }, (r2) => {
      if (r2.status === Office.AsyncResultStatus.Succeeded) {
        info(SOURCE, `showActionBar(${notificationId}): fallback InformationalMessage exibida.`);
      } else {
        warn(SOURCE, `showActionBar(${notificationId}): fallback também falhou (${r2.error?.message}).`);
      }
    });
  });
}

/**
 * Exibe uma barra de notificação informativa persistente no item de email.
 *
 * @param message - A mensagem de notificação a ser exibida.
 * @param notificationId - Identificador único da notificação.
 *
 * @example
 * ```typescript
 * showInfoBar("Assinatura gerenciada automaticamente.", NOTIFICATION_IDS.MANAGED);
 * ```
 */
export function showInfoBar(message: string, notificationId: string): void {
  const item = Office.context.mailbox.item;
  if (!item) {
    warn(SOURCE, `showInfoBar(${notificationId}): item nulo — notificação não exibida.`);
    return;
  }

  info(SOURCE, `showInfoBar(${notificationId}): exibindo InformationalMessage persistente.`);
  item.notificationMessages.addAsync(notificationId, {
    type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
    message: message,
    icon: "Icon.16x16",
    persistent: true,
  }, (r) => {
    if (r.status === Office.AsyncResultStatus.Succeeded) {
      info(SOURCE, `showInfoBar(${notificationId}): exibida com sucesso.`);
    } else if (r.error) {
      warn(SOURCE, `showInfoBar(${notificationId}): falhou — ${r.error.message}`);
    }
  });
}

/**
 * Exibe uma barra de erro persistente no item de email.
 *
 * @param message - A mensagem de erro a ser exibida.
 * @param notificationId - Identificador único da notificação.
 *
 * @example
 * ```typescript
 * showErrorBar("Falha ao inserir a assinatura.", NOTIFICATION_IDS.INSERT_FAILED);
 * ```
 */
export function showErrorBar(message: string, notificationId: string): void {
  const item = Office.context.mailbox.item;
  if (!item) {
    warn(SOURCE, `showErrorBar(${notificationId}): item nulo — notificação não exibida.`);
    return;
  }

  info(SOURCE, `showErrorBar(${notificationId}): exibindo InformationalMessage de erro.`);
  item.notificationMessages.addAsync(notificationId, {
    type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
    message: message,
    icon: "Icon.16x16",
    persistent: true,
  }, (r) => {
    if (r.status === Office.AsyncResultStatus.Succeeded) {
      info(SOURCE, `showErrorBar(${notificationId}): exibida com sucesso.`);
    } else if (r.error) {
      warn(SOURCE, `showErrorBar(${notificationId}): falhou — ${r.error.message}`);
    }
  });
}
